import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { LayoutDashboard, MessageSquare, LogOut, Menu, TrendingUp, Clock, ExternalLink, Users, Settings, Cable } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AppShell = ({ children }) => {
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
        <div className="p-6 h-20 flex items-center">
            <img src="/assets/logo.png" alt="AtCoastal.AI" className="h-8 w-auto opacity-95" />
        </div>
        
        <nav className="flex-1 px-4 space-y-2 mt-4">
          <div className="px-2 mb-2 text-xs font-medium text-white/40 uppercase tracking-wider">Menu</div>
          <NavLink
            to="/chat"
            onClick={() => setIsMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive 
                  ? 'bg-white/10 text-white shadow-lg shadow-black/20 ring-1 ring-white/10' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`
            }
          >
            <MessageSquare className="h-4 w-4" />
            Chat Assistant
          </NavLink>

          <NavLink
            to="/history"
            onClick={() => setIsMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive 
                  ? 'bg-white/10 text-white shadow-lg shadow-black/20 ring-1 ring-white/10' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`
            }
          >
            <Clock className="h-4 w-4" />
            My History
          </NavLink>
          
          <a
            href="https://notebooklm.google.com/notebook/fbe7fe0f-bc25-40ee-9cb9-3490f92b5625"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsMobileOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-all duration-200 group"
          >
            <div className="h-4 w-4 opacity-70 grayscale group-hover:grayscale-0 transition-all flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
            </div>
            <span>NotebookLM</span>
            <ExternalLink className="h-3 w-3 opacity-50 ml-auto" />
          </a>
          
          <NavLink
            to="/settings"
            onClick={() => setIsMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive 
                  ? 'bg-white/10 text-white shadow-lg shadow-black/20 ring-1 ring-white/10' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`
            }
          >
            <Settings className="h-4 w-4" />
            Settings
          </NavLink>
          
          {user?.role === 'admin' && (
            <>
              <div className="px-2 mt-6 mb-2 text-xs font-medium text-white/40 uppercase tracking-wider">Admin</div>
              
              <NavLink
                to="/admin"
                onClick={() => setIsMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive 
                      ? 'bg-white/10 text-white shadow-lg shadow-black/20 ring-1 ring-white/10' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <LayoutDashboard className="h-4 w-4" />
                Knowledge Base
              </NavLink>

              <NavLink
                to="/connections"
                onClick={() => setIsMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive 
                      ? 'bg-white/10 text-white shadow-lg shadow-black/20 ring-1 ring-white/10' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <Cable className="h-4 w-4" />
                Connections
              </NavLink>

              <NavLink
                to="/users"
                onClick={() => setIsMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive 
                      ? 'bg-white/10 text-white shadow-lg shadow-black/20 ring-1 ring-white/10' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <Users className="h-4 w-4" />
                Users
              </NavLink>

              <NavLink
                to="/insights"
                onClick={() => setIsMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive 
                      ? 'bg-white/10 text-white shadow-lg shadow-black/20 ring-1 ring-white/10' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <TrendingUp className="h-4 w-4" />
                Search Insights
              </NavLink>
            </>
          )}
        </nav>
        
        <div className="p-4 mt-auto">
           <div className="px-4 py-4 rounded-2xl bg-gradient-to-br from-white/5 to-transparent border border-white/5">
              <div className="flex items-center gap-3 mb-3">
                 <div className="h-8 w-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-xs font-bold border border-cyan-500/30 overflow-hidden">
                    {user?.picture ? (
                        <img src={user.picture} alt="Avatar" className="h-full w-full object-cover" />
                    ) : (
                        <span>{user?.name?.charAt(0).toUpperCase() || 'U'}</span>
                    )}
                 </div>
                 <div className="text-xs overflow-hidden">
                    <div className="text-white font-medium truncate w-32">{user?.name || 'User'}</div>
                    <div className="text-white/40 capitalize">{user?.role || 'Agent'}</div>
                 </div>
              </div>
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-2 h-8 text-xs text-white/60 hover:text-white hover:bg-white/5" 
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="h-3 w-3" />
                Sign Out
              </Button>
           </div>
        </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[rgb(var(--bg-0))] overflow-hidden text-white font-sans selection:bg-cyan-500/30">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex flex-col w-72 h-[calc(100vh-2rem)] fixed left-4 top-4 lg-glass rounded-3xl z-30">
        <NavContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full md:ml-80 relative overflow-hidden">
         {/* Top Bar (Mobile) */}
         <header className="h-16 md:hidden flex items-center justify-between px-4 bg-[rgb(var(--bg-0))] border-b border-white/10 sticky top-0 z-20">
             <img src="/assets/logo.png" alt="AtCoastal" className="h-6 w-auto" />
             
             <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
               <SheetTrigger asChild>
                 <Button variant="ghost" size="icon" className="text-white">
                     <Menu className="h-6 w-6" />
                 </Button>
               </SheetTrigger>
               <SheetContent side="left" className="bg-[rgb(var(--bg-0))] border-r-white/10 p-0 w-72">
                 <NavContent />
               </SheetContent>
             </Sheet>
         </header>

         <div className="flex-1 overflow-hidden relative p-0 md:p-4">
            {children}
         </div>
      </main>
    </div>
  );
};

export default AppShell;
